import { useState, useEffect } from "react";
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import styled from "styled-components";
import { getTipstersSelection, getMeetingPmPoolStatus } from "@/app/utils/request/requestList";
import { useMyContext } from "@/app/utils/MyContext";
import _ from 'lodash';
import { formattedDate } from "@/app/utils/DayJS";
import { textSize, text_size } from "@/app/utils/MixinSize";
import { Box } from "@mui/material";
import Link from '@mui/material/Link';
import { getJcbwDomain, getSearchParams, setRacesDetailLoading } from "@/app/utils/Common";
import NoInformation from "@/app/ui/Common/NoInformation";
import BetNow from "@/app/ui/Common/SvgComponent/BetNow";
import { useLoading } from "@/app/utils/LoadingContext";

const MeetingContainer = styled.div`
  width: 100%;
  ${text_size(13)}
  font-weight: 500;
  background-color: #FFFFFF;
  padding: 2px 0;
`;

const Title = styled.div`
  width: 100%;
  height: 36px;
  line-height: 16px;
  padding: 12px;
  ${text_size(15)}
  font-weight: 500;
  background-color: #F4F4F4;
  color: #333333
`;

const RaceBox = styled(Box)(() => ({
    [`&`]: {
        backgroundColor: "#FFFFFF",
        width: "calc(100% - 24px)",
        margin: "12px",
        border: "var(--border)",
        borderCollapse: "separate",
        borderRadius: "4px",
        overflow: "hidden",
        boxShadow: "0px 2px 10px 0px rgb(0 0 0 / 10%)",
    }
}));

const RaceHeader = styled(Box)(() => ({
    [`&`]: {
        color: "#FFFFFF",
        backgroundColor: "#6176A0",
        fontWeight: 500,
        padding: "8px 100px 8px 8px",
        ...textSize(13),
        lineHeight: "16px",
        textAlign: "left",
        minHeight: "48px",
        display: "flex",
        alignContent: "center",
        alignItems: "center",
        position: "relative",
    }
  }));


const TipsterTableContainer = styled(TableContainer)(() => ({
    [`&`]: {
        overflowX: "auto",      
    }
}));

const TipsterSubHeaderRow = styled(TableRow)(() => ({
    [`& td`]: {
        backgroundColor: "#E1E1E1",
    }
}));

const TipsterSelectionRow = styled(TableRow)(() => ({
    [`&[data-county="JPN"]:not(:last-child) ${TipsterSelectionCell}`]: {
        borderBottom: "1px solid #CCCCCC"
    },
    [`&:not([data-county="JPN"]):nth-child(odd) ${TipsterSelectionCell}`]: {
        backgroundColor: "#F4F4F4"
    },
}));

const TipsterSubHeader = styled(TableCell)(() => ({
    [`&`]: {
        color: "#333333",
        fontWeight: 400,
        ...textSize(11),
        lineHeight: "14px",
        padding: "8px",
    }
}));

const TipsterSelectionCell = styled(TableCell)(() => ({
    [`&`]: {
        color: "#333333",
        backgroundColor: "#FFFFFF",
        fontWeight: 500,
        ...textSize(13),
        lineHeight: "18px",
        padding: "8px",
        height: "44px",
        alignContent: "center",
        minWidth: "200px"
    }
}));

const BetNowLink = styled(Link)(() => ({
    [`&`]: {
        position: "absolute",
        top: "8px",
        right: "8px",
    }
}));

interface propType {
  setRacesDetailLoading: (loading: boolean) => void;
    type?: string;
}

const TipstersSelection: React.FC<propType> = (props: propType) => {
    const { completeLoading } = useLoading();
    const { state: myContextState, dispatch: myContextDispatch } = useMyContext();
    const { state: lang } = useLanguage();
    const [racesSelections, setRacesSelections] = useState<any>(null);
    const [isLoading, setLoading] = useState(true);
    const { b_cid } = getSearchParams(new URLSearchParams(window.location.search));
    const { t } = lang;

    const transformPmPoolsData = (data: any) => {
        const venueCode = data?.venueCode;
        const pmPools = data?.pmPools;
        return pmPools && venueCode ? pmPools.map((pool: any) => ({
          race: `${venueCode}-${pool.leg.races[0]}`,
          status: pool.status,
        })) : [];
    };
    
    const getStatusByRace = (race: string, pmPools: any[]): boolean => {
        const raceInfo = pmPools?.find(r => r.race === race);
        return raceInfo ? raceInfo.status === "START_SELL" : false;
    };

    const getBetLink = (item:string, langType:string) => {
        let lang = (langType === LANGTYPE_EN ? "/en" : "/ch");
        let raceNo = item && item.includes("-") && item.split("-").length == 2 ? item.split("-")[1] : "1";
        return getJcbwDomain() + lang + "/racing/wp/" + formattedDate(myContextState.selectedMeetingDate, "YYYY-MM-DD") + '/' + myContextState.selectedMeeting?.meetingVenueCode + '/' + raceNo + '?b_cid=SPLDSP_racing/simulcast-Tipster_BetNow';
    }
    const checkRaceinformationHasData = (data: any) => {
        const races = data.item?.races;
        const HasData = races?.filter((race:any) => race?.race?.information != null)?.length > 0;
        return HasData
    }
    const getRacesSelections = (data: any, pmPools: any[]) => {
        const races = data.item?.races;
        return ( races ?
          <>
            {races.map((race: { race: { information: { name: { value: string; }; raceNo: { value: string; }; }; }; selections: any[]; }, index: any) => {
              const raceName = race.race.information?.name?.value;
              const raceNo = race.race.information?.raceNo?.value ?? "";
              const selections = race.selections?.map(sel => sel.selection.value.split('\r\n'));
              const poolStatus = getStatusByRace(raceNo, pmPools);
            
              return ( race.race.information != null ?
                <RaceBox key={index}>
                    <RaceHeader>{raceNo} {raceName}<BetNowLink href={getBetLink(raceNo, lang.langType)} target="_blank"><BetNow sx={{display: poolStatus ? "flex" : "none", width: "63px", height: "29px"}}></BetNow></BetNowLink></RaceHeader>
                    <TipsterTableContainer>
                        <Table>
                            {
                                selections.map((selection, selectIndex) => {
                                    const headers = selection[0]?.split('\t\t');
                                    const mediaHeaders = selection[1]?.split('\t\t');
                                    //const rows = selection.slice(2).map((row: string) => row.split('\t'));
                                    const rows = selection.slice(2).map((line: string) => {
                                        return line.split('\t').map((item, index) => `${item} ${line.split('\t')[index + 1]}`).filter((_, i) => i % 2 === 0);
                                    });
                                    
                                    return ( headers.length > 1 ?
                                        <TableBody>
                                            <TipsterSubHeaderRow>
                                                {headers.map((header: string, headerIndex: any) => (
                                                    <TipsterSubHeader key={headerIndex}>{header}<br/>{mediaHeaders[headerIndex]}</TipsterSubHeader>
                                                ))}
                                            </TipsterSubHeaderRow>
                                            {rows.map((row: any[], rowIndex: any) => (
                                                <TipsterSelectionRow key={rowIndex}>
                                                    {row.map((cell: string, cellIndex: any) => (
                                                        <TipsterSelectionCell key={cellIndex}>{cell}</TipsterSelectionCell>
                                                    ))}
                                                </TipsterSelectionRow>
                                            ))}
                                        </TableBody> : <></>
                                )})
                            }
                        </Table>
                    </TipsterTableContainer>
                </RaceBox>:<></>
              );
            })}
          </> : null
        );
      };

    useEffect(() => {
        if (myContextState.isLoadingRaces) return;
        if (!myContextState.selectedMeeting?.displayName) {
            setRacesDetailLoading(props.setRacesDetailLoading)
            completeLoading('page');
            return
        }
        getTipstersSelection(lang.langCode, myContextState.season, myContextState.selectedMeeting?.displayName)
        .then((res: any) => {
            if (!res?.item) {
                setRacesSelections(null)
                setRacesDetailLoading(props.setRacesDetailLoading)
                completeLoading('page');
                return
            }
            getMeetingPmPoolStatus(
                formattedDate(myContextState.selectedMeetingDate, "YYYY-MM-DD"),
                myContextState.selectedMeetingVenueCode
            )
            .then((re: any) => {
                const pmPoolStatus = transformPmPoolsData(re?.raceMeetingProfile?.[0]);
                if (checkRaceinformationHasData(res)) {
                    setRacesSelections(getRacesSelections(res, pmPoolStatus));
                }
            }).finally(() =>{
                setRacesDetailLoading(props.setRacesDetailLoading)
                completeLoading('page');
            });
        })
        .catch((e: any) => {
            console.log(e);
            setRacesDetailLoading(props.setRacesDetailLoading)
            completeLoading('page');
        })
      }, [myContextState.isLoadingRaces, myContextState.selectedMeeting]);
      
    return (
        <>
            {props.type === "pc" ? (
                <Title id="TipstersSelection_Title"> {t('tipstersSelection_title')} </Title>
            ) : (<></>)
            }
            <MeetingContainer id="TipstersSelection_Meeting_Container" style={{ borderRadius: "0px 0px 4px 4px", boxShadow: "0px 2px 10px 0px rgb(0 0 0 / 10%)" }}>
                {
                    racesSelections ? racesSelections : <NoInformation />
                }
            </MeetingContainer>
        </>
    )
}

export default TipstersSelection; 